import { ContractsDetails } from '../Parsers/ContractParser';
import { SignDetails } from '../Parsers/SignParser';
import { UserDetails } from '../Parsers/UserParser';
import { postMessageSender } from '../PostMessage';
import { InstantLoanDashboard, InstantLoanPaymentDate } from './types';
import type middlewareOpenapi from './middleware-openapi';
import { createClient, NormalizeOAS } from 'fets';

const getUrl = () => {
  if (process.env.REACT_APP_MARIA_BASE_URL === undefined || process.env.REACT_APP_MARIA_BASE_URL === '') {
    console.warn('process.env.REACT_APP_MARIA_BASE_URL not set.');
  }
  return process.env.REACT_APP_MARIA_BASE_URL;
};

const url = getUrl();

// @ts-ignore - TS 4 build fails with this, ignoring the error for the build, if using TS 5 in VS Code you will still have typing when using the client
const middlewareApiClient = createClient<NormalizeOAS<typeof middlewareOpenapi>>({
  endpoint: url,
});

const getDashboardHeaders = (token: string): any => {
  return {
    Authorization: `Bearer ${token}`,
    'Application-ID': 'Nt9b58DxJn$nL6bRA7r027JX8cpewoM%O$cf3$t9^NogqiEwR*',
    'Content-Type': 'application/json',
  };
};

export const getDashboard = async (token?: string): Promise<InstantLoanDashboard | null> => {
  try {
    const res = await fetch(url + '/user/dashboard/instantloan', {
      method: 'GET',
      headers: getDashboardHeaders(token ?? ''),
    });
    if (res.ok) {
      const jsonData = await res.json();

      const result = jsonData.result;
      const dashboard: InstantLoanDashboard = result;

      return dashboard;
    } else {
      const jsonData = await res.json();

      postMessageSender({
        status: 'error',
        data: jsonData.error,
      });
    }
  } catch (error) {
    console.warn('error: ', error);
    postMessageSender({
      status: 'error',
      data: {
        code: 500,
        message: 'Sorry, something went wrong and we were unable to complete the signature step. Please try again later or contact support.',
      },
    });
  }
  return null;
};

export const getPaymentDate = async (token?: string, loanId?: number): Promise<InstantLoanPaymentDate | null> => {
  try {
    const res = await fetch(url + `/loan/non-pay-date?loanId=${loanId}`, {
      method: 'GET',
      headers: getDashboardHeaders(token ?? ''),
    });
    if (res.ok) {
      const jsonData = await res.json();

      const result = jsonData.result;
      const paymentDate: InstantLoanPaymentDate = result;

      return paymentDate;
    } else {
      return null;
    }
  } catch (error) {
    console.warn('error: ', error);
  }
  return null;
};

export const getContracts = async (token: string, loanId?: number): Promise<ContractsDetails | null> => {
  try {
    if (!loanId) {
      throw new Error('Loan id should not be undefined');
    }

    const res = await middlewareApiClient['/v3/loans/{loanId}/contract/blank'].get({
      headers: getDashboardHeaders(token),
      params: { loanId: loanId },
      query: { binary: true },
    });

    if (res.ok) {
      const json = await res.json();
      return {
        contract: json.contract,
        pad: json.pad ?? '',
      };
    } else {
      const error = await res.json();
      postMessageSender({
        status: 'error',
        data: {
          code: error.statusCode,
          message: error.message,
        },
      });
    }
  } catch (error) {
    console.warn('error: ', error);
    postMessageSender({
      status: 'error',
      data: {
        code: 500,
        message: 'Sorry, something went wrong and we were unable to complete the signature step. Please try again later or contact support.',
      },
    });
  }
  return null;
};

type SignBodyType = {
  loanId: number;
  signature1: string;
  initial: string;
  signature2: string;
};

export const changeDate = async (token?: string, loanId?: number) => {
  try {
    const response = await fetch(`${url}/loan/non-pay-date`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Application-ID': 'Nt9b58DxJn$nL6bRA7r027JX8cpewoM%O$cf3$t9^NogqiEwR*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ loanId: loanId }),
    });

    if (response.ok) {
      return { success: true };
    }
    const data = await response.json();
    return { success: false, message: (data.error?.message as string) ?? 'Internal error' };
  } catch (error) {
    return { success: false, message: 'Internal error' };
  }
};

export const sign = async (token: string, data: SignBodyType): Promise<{ result?: SignDetails; error?: { message: string; code: number } }> => {
  for (let i = 0; i < 2; i++) {
    try {
      const res = await middlewareApiClient['/v3/loans/{loanId}/contract/sign'].post({
        headers: getDashboardHeaders(token),
        params: { loanId: data.loanId },
        json: {
          signature1: data.signature1,
          signature2: data.signature2,
          initials: data.initial,
          lang: 'en',
        },
      });

      if (res.status === 409) {
        postMessageSender({
          status: 'return',
        });
      }

      if (!res.ok) {
        const jsonData = await res.json();
        console.error('Response error: ', jsonData);
        return {
          error: {
            code: jsonData.statusCode ?? 0,
            message: 'Sorry, something went wrong and we were unable to complete the signature step. Please try again later or contact support.',
          },
        };
      }

      const signDetails: SignDetails = await res.json();

      return {
        result: signDetails,
      };
    } catch (error) {
      console.warn('error: ', error);
    }

    console.log('Retrying to sign...');
  }

  return {
    error: {
      code: 500,
      message: 'Sorry, something went wrong and we were unable to complete the signature step. Please try again later or contact support.',
    },
  };
};

const getUserInfoHeaders = (token: string): any => {
  return {
    Authorization: `Bearer ${token}`,
    'Application-ID': 'Nt9b58DxJn$nL6bRA7r027JX8cpewoM%O$cf3$t9^NogqiEwR*',
  };
};

export const getUserInfo = async (token?: string): Promise<UserDetails | null> => {
  try {
    const res = await fetch(url + '/user/info', {
      method: 'GET',
      headers: getUserInfoHeaders(token ?? ''),
    });
    if (res.ok) {
      const jsonData = await res.json();
      if (jsonData.result) {
        const result = jsonData.result;
        const user: UserDetails = result;

        return user;
      }
      return null;
    } else {
      const jsonData = await res.json();

      postMessageSender({
        status: 'error',
        data: jsonData.error,
      });
    }
  } catch (error) {
    console.warn('error: ', error);
    postMessageSender({
      status: 'error',
      data: {
        code: 500,
        message: 'Sorry, something went wrong and we were unable to complete the signature step. Please try again later or contact support.',
      },
    });
  }
  return null;
};

export const getLoanLTV = async (token: string, loanId: number) => {
  const res = await fetch(`${url}/application/loan/${loanId}/ltv`, {
    method: 'GET',
    headers: {
      ...getUserInfoHeaders(token ?? ''),
      'Content-Type': 'application/json',
    },
  });

  const jsonData = await res.json();

  if (res.ok) return jsonData.result.ltv as number;

  return null;
};
